import React, { useState } from "react";
import { ActionIcon, CopyButton } from "@mantine/core";
import {
  IconCheck,
  IconCopy,
  IconEye,
  IconEyeClosed,
} from "@tabler/icons-react";
import { navigateToField } from "@pages/ClientProfile/utils.tsx";
import { FieldError } from "@pages/ClientProfile/components/TaxInfo/items/FieldError.tsx";
import { useProfileStore } from "@pages/ClientProfile/store/profileStore.ts";
import { AppTooltip } from "@components/Tooltip/AppTooltip.tsx";

interface TaxInfoFieldProps {
  keyName: string;
  value: any;
  uniqueKey: string;
  shouldIncludeField: (key: string) => boolean;
  isSensitiveField: (key: string) => boolean;
  formatKey: (key: string) => string;
}

/**
 * Displays a field on the client profile while in preview mode.
 */
const TaxInfoField: React.FC<TaxInfoFieldProps> = ({
  keyName,
  value,
  uniqueKey,
  shouldIncludeField,
  isSensitiveField,
  formatKey,
}) => {
  const [revealedFields, setRevealedFields] = useState<Record<string, boolean>>(
    {}
  );
  const { setReview } = useProfileStore();

  // Check for empty values
  if (
    !shouldIncludeField(keyName) ||
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === "object" && Object.keys(value).length === 0)
  ) {
    return null;
  }

  const isRevealed = revealedFields[uniqueKey];
  const isSensitive = isSensitiveField(keyName);

  // Content to display for the field
  const content = Array.isArray(value) ? (
    value.map((val, idx) => (
      <div
        key={`${uniqueKey}-${idx}`}
        className="tax-section-data-value"
        style={{ whiteSpace: "normal", wordBreak: "break-word" }}
      >
        {val && typeof val === "object"
          ? Object.entries(val)?.map(([subKey, subValue]) => (
              <div key={subKey}>
                <strong>{formatKey(subKey)}:</strong> {String(subValue)}
              </div>
            ))
          : null}
      </div>
    ))
  ) : (
    <div
      className="tax-section-data-value"
      style={{ whiteSpace: "normal", wordBreak: "break-word" }}
    >
      {String(value)}
    </div>
  );

  const handleToggleReveal = () => {
    setRevealedFields((prev) => ({
      ...prev,
      [uniqueKey]: !prev[uniqueKey],
    }));
  };

  // Handles moving to review mode if you double click the field.
  const handleDoubleClick = async () => {
    setReview(true);
    requestAnimationFrame(async () => {
      await navigateToField(uniqueKey);
    });
  };

  /**
   * Formats the value if the keyName includes "phone"
   * @param value - The value to format
   * @returns The formatted value
   */
  const formatValueIfPhone = (value: string) => {
    if (keyName.toLowerCase().includes("phone")) {
      // Remove parentheses and format phone number
      const formattedValue = value.replace(/[()]/g, "").replace(/\s+/g, "");
      return `${formattedValue.slice(0, 3)}-${formattedValue.slice(
        3,
        6
      )}${formattedValue.slice(6)}`;
    }
    return value;
  };

  return (
    <FieldError
      fieldKey={uniqueKey}
      onDoubleClick={handleDoubleClick}
      className="tax-section-data-item"
    >
      <div className="tax-section-data-label">
        {formatKey(keyName)}
        {!isSensitive && (
          <AppTooltip
            labels={["Copy to clipboard"]}
            color="black"
            position="right"
            textColor="white"
          >
            <span>
              <CopyButton value={formatValueIfPhone(String(value))}>
                {({ copied, copy }) => (
                  <ActionIcon
                    color="#757575"
                    variant="transparent"
                    onClick={copy}
                  >
                    {copied ? <IconCheck size={20} /> : <IconCopy size={20} />}
                  </ActionIcon>
                )}
              </CopyButton>
            </span>
          </AppTooltip>
        )}
      </div>
      <div className="tax-section-data-content">
        {isSensitive ? (
          <button
            className="sensitive-field"
            onClick={handleToggleReveal}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleToggleReveal();
              }
            }}
          >
            <span
              style={{
                letterSpacing: !isRevealed ? "0.15rem" : "0rem",
              }}
              className="sensitive-field-value"
            >
              {isRevealed ? value : "••••••••"}
            </span>
            {isRevealed ? <IconEyeClosed size={20} /> : <IconEye size={20} />}
          </button>
        ) : (
          content
        )}
      </div>
    </FieldError>
  );
};

export default TaxInfoField;
